import {
    Maskito,
    maskitoInitialCalibrationPlugin,
    MaskitoOptions,
    maskitoTransform,
    MaskitoElement,
    maskitoAdaptContentEditable,
} from '@maskito/core';
import { RefObject, useMemo, useRef, useState } from 'react';
import { useRefCallback, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import { MaskConfig } from './types';

// в jsdom окружении не работает e.isContentEditable заменяем на эту функцию
const isContentEditable = (element: HTMLElement) =>
    element.getAttribute('contenteditable') === 'true' || element.isContentEditable;

const MASKITO_DEFAULT_ELEMENT_PREDICATE: (element: HTMLElement) => MaskitoElement = e =>
    isContentEditable(e)
        ? maskitoAdaptContentEditable(e)
        : e.querySelector<HTMLInputElement | HTMLTextAreaElement>('input,textarea') ||
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приведение типов из маскито
          (e as HTMLInputElement | HTMLTextAreaElement);

/**
 * Хук, навешивает обработчики Maskito на переданный input
 * @description Для того чтобы работало controlled поведение, нужно использовать onInput callback
 * @param options Опции маски маскито
 * @returns ref callback, навешиваемый на элемент, и isMaskInited - информацию об инициализированности маски
 */
const useMaskito = ({ options }: { options: MaskitoOptions }): RefObject<HTMLInputElement | HTMLTextAreaElement> => {
    const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const newOptions: MaskitoOptions = useMemo(() => {
        return {
            ...options,
            plugins: [...(options.plugins ? options.plugins : []), maskitoInitialCalibrationPlugin()],
        };
    }, [options]);

    useIsomorphicLayoutEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        const maskedElement = new Maskito(MASKITO_DEFAULT_ELEMENT_PREDICATE(element), newOptions);
        return () => {
            maskedElement.destroy();
        };
    }, [newOptions]);

    return ref;
};

interface UseControlledMaskitoProps {
    maskConfig: MaskConfig;
    value?: string;
    onChange?: (value: string) => void;
}

/**
 * Хук, навешивает обработчики Maskito на переданный input,
 * а также управляет controlled состоянием маскированного инпута
 * @description Принимает обработчики, конфиг маски и value, отдает те пропсы, которые нужно прокинуть в компонент
 * @param props Конфиг маски, onChange, value
 * @returns ref callback, навешиваемый на элемент, onChange (React'овый onInput), value
 */
export const useControlledMaskito = (props: UseControlledMaskitoProps) => {
    const { maskConfig, value, onChange } = props;

    const [maskedValue, setMaskedValue] = useState<string | undefined>(() =>
        // Выставляем маскированное значение (при первой отрисовке в Maskito попадет маскированное значение)
        value ? maskitoTransform(value, maskConfig.maskitoOptions) : value,
    );
    const lastAcceptedUnmaskedValue = useRef<string | undefined>();

    const onMaskedValueChange = useRefCallback((maskedValue: string) => {
        setMaskedValue(maskedValue);
        const unmaskedValue = maskConfig.transformToUnmasked(maskedValue);
        // В случае, если после применения маски unmaskedValue не поменялся, то onChange не триггерим
        if (unmaskedValue !== lastAcceptedUnmaskedValue.current) {
            lastAcceptedUnmaskedValue.current = unmaskedValue;
            if (unmaskedValue !== value) {
                onChange?.(unmaskedValue);
            }
        }
    });

    const ref = useMaskito({
        options: maskConfig.maskitoOptions,
    });

    // Необходимо чтобы не триггерить изменением маски useLayoutEffect, реинициализация будет происходить в maskito
    const maskConfigRef = useRef(maskConfig);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    maskConfigRef.current = maskConfig;

    // Хук отслеживает изменения value извне
    useIsomorphicLayoutEffect(() => {
        // Ждем пока Maskito инициализируется
        if (!ref.current) {
            return;
        }
        // Следим только за изменением value
        if (value !== undefined && lastAcceptedUnmaskedValue.current !== value) {
            lastAcceptedUnmaskedValue.current = value;
            const newMaskedValue = maskitoTransform(value, maskConfigRef.current.maskitoOptions);

            onMaskedValueChange(newMaskedValue);
        }
    }, [onMaskedValueChange, ref, value]);

    return { ref, onChange: onMaskedValueChange, value: maskedValue };
};
